/**
 * TV Guide — full redesign matching the user's mockup.
 *
 * Layout:
 *   • Left rail (TV-guide-only sidebar): TAP IN TV GUIDE logo + nav with
 *     Home / Live TV / On Demand / My List / Search / Settings + per-channel
 *     pills (101-105) + category links.
 *   • Hero banner top-right: image OR video, LIVE NOW pill, title +
 *     subtitle, Watch Now / More Info CTAs. Set by admin via /admin/tv-guide.
 *   • Today on TAP IN TV: 5-row × N-column schedule grid auto-generated
 *     from /api/tv-guide/schedule. Current slot has red border + LIVE pill.
 *   • Featured Shows rail at the bottom — 6 admin-curated cards.
 *
 * All times render in the viewer's local timezone (Date.toLocaleTimeString
 * auto-detects).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Tv, Film, ListPlus, Search, Settings, Play, Info, Radio, Music, Trophy } from "lucide-react";
import api from "../lib/api";

const NAV_ITEMS = [
  { to: "/",            icon: Home,     label: "Home",      testid: "tv-guide-nav-home" },
  { to: "/live",        icon: Tv,       label: "Live TV",   testid: "tv-guide-nav-live" },
  { to: "/browse",      icon: Film,     label: "On Demand", testid: "tv-guide-nav-ondemand" },
  { to: "/watchlist",   icon: ListPlus, label: "My List",   testid: "tv-guide-nav-list" },
  { to: "/search",      icon: Search,   label: "Search",    testid: "tv-guide-nav-search" },
  { to: "/settings",    icon: Settings, label: "Settings",  testid: "tv-guide-nav-settings" },
];

const CATEGORIES = [
  { to: "/browse?category=music",  icon: Music,  label: "Music"     },
  { to: "/browse?category=sports", icon: Trophy, label: "Sports"    },
  { to: "/browse?category=films",  icon: Film,   label: "Movies"    },
  { to: "/radio",                  icon: Radio,  label: "Radio"     },
  { to: "/originals",              icon: Tv,     label: "Originals" },
];

function fmtLocalTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toUpperCase();
  } catch { return ""; }
}

export default function TvGuide() {
  const [hero, setHero] = useState(null);
  const [channels, setChannels] = useState([]);
  const [slots, setSlots] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/tv-guide/hero").then((r) => r.data).catch(() => null),
      api.get("/tv-guide/channels").then((r) => r.data || []).catch(() => []),
      api.get("/tv-guide/schedule").then((r) => r.data || []).catch(() => []),
      api.get("/tv-guide/featured").then((r) => r.data || []).catch(() => []),
    ]).then(([h, c, s, f]) => {
      setHero(h); setChannels(c); setSlots(s); setFeatured(f);
    }).finally(() => setLoading(false));
  }, []);

  // Group slots by channel
  const byChannel = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const k = s.channel_number;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    return m;
  }, [slots]);

  return (
    <div data-testid="tv-guide-page" className="min-h-screen bg-black text-white pt-24">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        {/* ───────── Left sidebar ───────── */}
        <aside data-testid="tv-guide-sidebar" className="sticky top-24 self-start hidden lg:block px-5 pt-6 pb-12 border-r border-white/10 min-h-[calc(100vh-6rem)]">
          <Link to="/" className="block">
            <img
              src="/tv-guide-logo.png?v=2"
              alt="TAP IN TV GUIDE"
              data-testid="tv-guide-logo"
              className="w-full max-w-[220px] object-contain drop-shadow-[0_0_20px_rgba(239,68,68,0.4)] mb-8 mx-auto"
            />
          </Link>

          <nav className="space-y-1.5">
            {NAV_ITEMS.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition ${it.label === "Home" ? "bg-red-600 text-white shadow-[0_0_24px_rgba(239,68,68,0.45)]" : "text-white/70 hover:text-white hover:bg-white/5"}`}
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </Link>
            ))}
          </nav>

          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-black mb-2 px-3">Channels</div>
            <div className="space-y-1">
              {channels.map((c) => (
                <button
                  key={c.id}
                  data-testid={`tv-guide-chan-${c.number}`}
                  onClick={() => {
                    const el = document.querySelector(`[data-channel-row="${c.number}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-bold text-white/60 hover:text-white hover:bg-white/5 transition"
                >
                  <span className="w-1.5 h-6 rounded-full" style={{ background: c.accent_color || "#EF4444" }} />
                  <span className="text-white/40 tabular-nums">{c.number}</span>
                  <span className="truncate">{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-black mb-2 px-3">Categories</div>
            <div className="space-y-1">
              {CATEGORIES.map((c) => (
                <Link key={c.to} to={c.to} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-bold text-white/60 hover:text-white hover:bg-white/5">
                  <c.icon className="w-4 h-4" /> {c.label}
                </Link>
              ))}
            </div>
          </div>
        </aside>

        {/* ───────── Main content ───────── */}
        <main className="px-4 sm:px-6 lg:px-10 pt-2 pb-20 max-w-[1500px]">
          {/* Hero banner */}
          <HeroBanner hero={hero} loading={loading} onWatch={(url) => nav(url || "/")} />

          {/* Schedule grid */}
          <section className="mt-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 data-testid="tv-guide-today-title" className="font-display font-black text-xl uppercase tracking-[0.2em]">Today on TAP IN TV</h2>
              <div className="text-white/50 text-xs">
                {new Date().toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}
              </div>
            </div>

            <div data-testid="tv-guide-schedule" className="space-y-3">
              {channels.map((c) => (
                <div key={c.id} data-channel-row={c.number} className="grid grid-cols-[88px_1fr] gap-3 items-stretch">
                  {/* Channel cell */}
                  <div className="bg-black border border-white/10 rounded-lg flex flex-col items-center justify-center py-2 px-1 text-center">
                    <div className="font-display font-black text-xs leading-none">
                      <span className="text-white">TAP</span>
                      <span className="ml-0.5 inline-block bg-red-600 text-white px-0.5 text-[10px] align-middle">IN</span>
                    </div>
                    <div className="text-[9px] font-black uppercase tracking-wider mt-0.5" style={{ color: c.accent_color || "#fff" }}>
                      {(c.name || "").replace(/^TAP IN /i, "")}
                    </div>
                    <div className="text-white/50 text-[10px] font-bold mt-1 tabular-nums">{c.number}</div>
                  </div>

                  {/* Slot row */}
                  <div className="flex gap-3 overflow-x-auto no-scrollbar">
                    {(byChannel.get(c.number) || []).map((s) => (
                      <SlotCard key={s.id} slot={s} accent={c.accent_color} />
                    ))}
                    {(byChannel.get(c.number) || []).length === 0 && (
                      <div className="flex-1 bg-white/[0.02] border border-dashed border-white/10 rounded-lg flex items-center justify-center text-white/30 text-xs">
                        No programming today
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Featured shows */}
          <section className="mt-12">
            <h2 data-testid="tv-guide-featured-title" className="font-display font-black text-xl uppercase tracking-[0.2em] mb-4">Featured Shows</h2>
            <div data-testid="tv-guide-featured" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {featured.map((s) => (
                <FeaturedShowCard key={s.id} show={s} />
              ))}
              {featured.length === 0 && !loading && (
                <div className="col-span-6 text-center text-white/40 py-10 text-sm">Featured shows coming soon</div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ─── Hero banner ─── */
function HeroBanner({ hero, loading, onWatch }) {
  if (loading) {
    return <div className="aspect-[21/9] rounded-2xl bg-white/5 animate-pulse" />;
  }
  const url = hero?.url;
  const cta = hero?.cta_url || "/";
  const title = hero?.title || "TAP IN TALK";
  const subtitle = hero?.subtitle || "Real conversations. Real story. No filter. Just facts.";

  return (
    <div data-testid="tv-guide-hero" className="relative aspect-[21/9] rounded-2xl overflow-hidden bg-black border border-white/10">
      {hero?.kind === "video" && url ? (
        <video src={url} poster={hero.poster_url} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
      ) : url ? (
        <img src={url} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-red-950 via-black to-amber-950" />
      )}
      {/* Left gradient + content */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
      <div className="relative z-10 h-full flex items-center px-6 sm:px-12 max-w-[640px]">
        <div>
          {hero?.is_live && (
            <div data-testid="tv-guide-hero-live" className="inline-flex items-center gap-1.5 mb-3">
              <span className="bg-red-600 text-white text-[10px] font-black uppercase tracking-[0.2em] px-2 py-1 rounded animate-pulse">{hero?.live_label || "LIVE NOW"}</span>
            </div>
          )}
          <h1 data-testid="tv-guide-hero-title" className="font-display font-black text-3xl sm:text-5xl lg:text-6xl leading-tight uppercase tracking-tight">
            {title}
          </h1>
          <p data-testid="tv-guide-hero-subtitle" className="text-white/80 mt-3 text-sm sm:text-base max-w-md leading-relaxed">{subtitle}</p>
          <div className="mt-5 flex items-center gap-3">
            <button
              data-testid="tv-guide-hero-watch"
              onClick={() => onWatch(cta)}
              className="bg-red-600 hover:bg-red-500 text-white font-black px-6 py-2.5 rounded-lg inline-flex items-center gap-2 shadow-[0_0_20px_rgba(239,68,68,0.45)]"
            >
              <Play className="w-4 h-4 fill-current" /> Watch Now
            </button>
            <Link
              data-testid="tv-guide-hero-info"
              to={cta || "/"}
              className="bg-white/10 hover:bg-white/15 text-white font-black px-6 py-2.5 rounded-lg inline-flex items-center gap-2 backdrop-blur"
            >
              <Info className="w-4 h-4" /> More Info
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Schedule slot card ─── */
function SlotCard({ slot, accent }) {
  const isLive = slot.is_live;
  const start = fmtLocalTime(slot.start_at);
  const end = fmtLocalTime(slot.end_at);
  const span = (() => {
    const a = new Date(slot.start_at).getTime();
    const b = new Date(slot.end_at).getTime();
    const mins = Math.max(30, Math.round((b - a) / 60000));
    return Math.min(420, Math.round(mins * 4)); // 4px per minute, capped
  })();
  return (
    <Link
      to={`/show/${slot.show_slug}`}
      data-testid={`tv-guide-slot-${slot.show_slug}`}
      className={`relative shrink-0 rounded-lg overflow-hidden border transition group ${isLive ? "border-red-500" : "border-white/10 hover:border-white/30"}`}
      style={{ width: `${span}px`, minWidth: "150px", height: "84px" }}
    >
      {slot.thumbnail_url ? (
        <img src={slot.thumbnail_url} alt={slot.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition" />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent || "#EF4444"}25, transparent 80%), #0a0a0a` }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
      <div className="relative h-full p-2.5 flex flex-col justify-between">
        <div>
          {isLive && (
            <span className="inline-block bg-red-600 text-white text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded mb-1">LIVE</span>
          )}
          <div className="font-display font-black text-xs uppercase tracking-wide leading-tight line-clamp-2">{slot.title}</div>
        </div>
        <div className="text-white/60 text-[10px] font-bold tabular-nums">{start} – {end}</div>
      </div>
    </Link>
  );
}

/* ─── Featured show card ─── */
function FeaturedShowCard({ show }) {
  return (
    <Link
      to={`/show/${show.slug}`}
      data-testid={`tv-guide-featured-${show.slug}`}
      className="relative aspect-[2/3] rounded-xl overflow-hidden border border-white/10 hover:border-white/40 group"
    >
      {show.poster_url ? (
        <img src={show.poster_url} alt={show.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition" />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${show.accent_color || "#EF4444"}33, #000 75%)` }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-3">
        <div className="font-display font-black text-sm uppercase tracking-wide leading-tight">{show.title}</div>
        <div className="inline-block mt-1.5 bg-red-600 text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded">New Episodes</div>
      </div>
    </Link>
  );
}
